import { useMemo, useState } from "react";
import { CheckIcon } from "@heroicons/react/24/outline";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import {
  categorizeWatchlist,
  type NewsquawkCategory,
} from "@/pages/PortfolioBuilder/newsquawkCategories";
import type { Portfolio } from "@/types/Portfolio";
import { relevantCategories } from "../profileMapping";
import { CARD, CARD_ACTIVE, SECTION_LABEL } from "../ui/theme";
import type { OnboardingAnswers } from "../types";

interface StepWatchlistsProps {
  answers: OnboardingAnswers;
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}

/**
 * Welcome flow step 2: pick starter watchlists from the Newsquawk prebuilt
 * library, most-relevant categories first. Selections are merged into the
 * primary headline wire as HIGHLIGHTS at apply time — they never filter the
 * feed, so there's no risk of over-narrowing a new user's view.
 */
export default function StepWatchlists({
  answers,
  selectedIds,
  onChange,
}: StepWatchlistsProps) {
  const { portfolios, portfoliosQuery } = usePortfoliosCRUD();

  const grouped = useMemo(() => {
    const prebuilt = portfolios.filter(
      (p: Portfolio) => p.owner_type === "NEWSQUAWK"
    );
    const byCategory = new Map<NewsquawkCategory, Portfolio[]>();
    for (const portfolio of prebuilt) {
      const category = categorizeWatchlist(portfolio);
      const list = byCategory.get(category) ?? [];
      list.push(portfolio);
      byCategory.set(category, list);
    }
    // Relevant categories first — then the rest, but only on request.
    //
    // Ordering alone was not enough: someone who picked Energy still had to
    // scroll past Utilities, Macro, Materials, Indices and Technology to see
    // the end of a list they had already told us was irrelevant. The answers
    // from step one are a filter, not a sort hint.
    const preferred = relevantCategories(answers.assetClasses);
    const ordered: { category: NewsquawkCategory; items: Portfolio[] }[] = [];
    for (const category of preferred) {
      const items = byCategory.get(category);
      if (items?.length) {
        ordered.push({ category, items });
        byCategory.delete(category);
      }
    }
    const rest: { category: NewsquawkCategory; items: Portfolio[] }[] = [];
    for (const [category, items] of byCategory) {
      if (items.length) rest.push({ category, items });
    }
    // No answers to filter on (skipped step one) means everything is relevant.
    return ordered.length > 0 ? { ordered, rest } : { ordered: rest, rest: [] };
  }, [portfolios, answers.assetClasses]);

  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? [...grouped.ordered, ...grouped.rest] : grouped.ordered;

  const toggle = (id: number) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id]
    );
  };

  return (
    <div>
      {portfoliosQuery.isLoading && (
        <p className="text-sm text-gray-500 animate-pulse">Loading the library…</p>
      )}

      {!portfoliosQuery.isLoading && visible.length === 0 && (
        <p className="text-sm text-gray-500">Nothing prebuilt right now — skip this step.</p>
      )}

      <div className="space-y-5">
        {visible.map(({ category, items }) => (
          <div key={category}>
            <h3 className={SECTION_LABEL}>{category}</h3>
            <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {items.map((portfolio) => {
                const selected = selectedIds.includes(portfolio.id);
                return (
                  <button
                    key={portfolio.id}
                    type="button"
                    onClick={() => toggle(portfolio.id)}
                    aria-pressed={selected}
                    className={`flex items-center justify-between gap-2 px-3.5 py-2.5 text-left text-sm transition-all duration-200 active:scale-[0.98] cursor-pointer ${
                      selected
                        ? `${CARD_ACTIVE} text-squawk-blue`
                        : `${CARD} text-gray-200 hover:border-white/20`
                    }`}
                  >
                    <span className="truncate">{portfolio.name}</span>
                    {selected && <CheckIcon className="h-4 w-4 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {!showAll && grouped.rest.length > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-5 cursor-pointer border-none bg-transparent p-0 text-sm text-squawk-blue transition-colors hover:text-squawk-blue/80"
        >
          Show the rest of the library
        </button>
      )}
    </div>
  );
}
